import { type TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TailwindElement } from '../../shared/tailwind.element';
import { questionaireContext } from '../../context/context';
import { consume } from '@lit/context';
import { type QuestionaireData } from '../../interfaces/interfaces';
import { CustomEventNames } from '../../constants/custom-event-names';
import { textItems } from '../../constants/text';

enum ButtonId {
	StartButton = 'startButton',
	AnswerButton = 'answerButton',
	OpenButton = 'openButton',
	ResumeButton = 'resumeButton',
}

function LoadingIndicator(buttonId: ButtonId) {
	return function (_target: unknown, _propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor {
		const originalMethod = descriptor.value;

		descriptor.value = function (this: { isLoading: boolean; loadingButton: ButtonId | null }, ...args: unknown[]) {
			this.loadingButton = buttonId;
			this.isLoading = true;
			return originalMethod.apply(this, args);
		};

		return descriptor;
	};
}

@customElement('szw-ba-action-bar')
export class SzwBaActionBar extends TailwindElement() {
	@consume({ context: questionaireContext, subscribe: true })
	@property({ attribute: false })
	data!: QuestionaireData;

	@state()
	isLoading = false;

	@state()
	showResultBtn = false;

	@state()
	isDisabled = false;

	@state()
	loadingButton: ButtonId | null = null;

	connectedCallback(): void {
		super.connectedCallback();
		window.addEventListener(CustomEventNames.LoadingDone, this.handleLoadingDone);
		window.addEventListener(CustomEventNames.BlockQuestionaire, () => {
			this.isDisabled = true;
		});
		window.addEventListener(CustomEventNames.UnblockQuestionaire, () => {
			this.isDisabled = false;
		});
		window.addEventListener(CustomEventNames.ShowResultBtn, (event: Event) => {
			const e = event as CustomEvent;
			this.showResultBtn = e.detail;
		});
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener(CustomEventNames.LoadingDone, this.handleLoadingDone);
		window.removeEventListener(CustomEventNames.BlockQuestionaire, () => {
			this.isDisabled = true;
		});
		window.removeEventListener(CustomEventNames.UnblockQuestionaire, () => {
			this.isDisabled = false;
		});
	}

	handleLoadingDone = (): void => {
		this.isLoading = false;
		this.loadingButton = null;
	};

	loaderTemplate(): TemplateResult {
		return html` <div class="flex items-center justify-center md:ml-2">
			<svg
				class="h-6 w-6 animate-spin text-white"
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				title="${textItems.icons.loading}"
			>
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path
					class="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
				></path>
			</svg>
		</div>`;
	}

	renderReportButtonTemplate(): TemplateResult {
		return html`<nav class="order-2 ml-auto flex flex-row-reverse gap-4">
				<button
					class="btn btn--primary"
					@click=${() => {
						window.print();
					}}
				>
					<span class="hidden md:block">${textItems.print}</span>
					<svg
						class="md:hidden"
						title="${textItems.print}"
						width="22"
						height="21"
						viewBox="0 0 22 21"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g>
							<g>
								<path d="M17 0H5V3H17V0Z" fill="white" />
								<path
									id="Vector_2"
									d="M21 4H1C0.5 4 0 4.4 0 5V13C0 13.6 0.4 14 1 14H4V15.6C4 15.9 4.1 16.1 4.3 16.3L8.7 20.7C8.9 20.9 9.1 21 9.4 21H17C17.6 21 18 20.6 18 20V14H21C21.6 14 22 13.6 22 13V5C22 4.4 21.5 4 21 4ZM16 19H10V16C10 15.4 9.6 15 9 15H6V11H16V19ZM20 8H18V6H20V8Z"
									fill="white"
								/>
							</g>
						</g>
					</svg>
				</button>
				<button
					class="btn btn--secondary"
					@click=${() => {
						this.closeQuestionnaire();
					}}
					?disabled=${this.isLoading}
				>
					${textItems.close}
				</button>
			</nav>
			${this.renderResumeButtonTemplate()}`;
	}

	renderStartButtonTemplate(): TemplateResult {
		return html`<nav class="ml-auto">
			<button
				class="btn btn--primary"
				@click=${() => {
					this.startQuestionnaire();
				}}
				?disabled=${this.isLoading}
			>
				<span class="hidden md:block">${textItems.start}</span>

				${this.isLoading && this.loadingButton === ButtonId.StartButton
					? html` ${this.loaderTemplate()} `
					: html`<svg
							class="md:hidden"
							width="16"
							height="24"
							viewBox="0 0 16 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							title="${textItems.start}"
					  >
							<g>
								<path
									d="M1.70022 23.1C0.700224 22.1 0.300224 20.8 1.40022 19.8L9.10022 12.1L1.30022 4.30002C0.300224 3.30002 0.600224 2.00002 1.60022 1.00002C2.60022 1.7941e-05 3.90022 -0.399982 4.90022 0.700018L14.5002 10.3C15.5002 11.3 15.5002 12.9 14.5002 13.9L5.00022 23.4C4.00022 24.4 2.70022 24.1 1.70022 23.1Z"
									fill="white"
								/>
							</g>
					  </svg>`}
			</button>
		</nav> `;
	}

	@LoadingIndicator(ButtonId.StartButton)
	startQuestionnaire(): void {
		const event = new CustomEvent(CustomEventNames.StartQuestionnaire, {
			detail: { message: 'Questionnaire started' },
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	@LoadingIndicator(ButtonId.AnswerButton)
	answerQuestion(): void {
		const event = new CustomEvent(CustomEventNames.SubmitForm, {
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	@LoadingIndicator(ButtonId.OpenButton)
	openQuestion(): void {
		const event = new CustomEvent(CustomEventNames.OpenQuestion, {
			detail: {
				responseCode: this.data.responseCode,
				questionId: this.data.previousQuestionId,
			},
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	resumeLater(): void {
		const event = new CustomEvent(CustomEventNames.ResumeLater, {
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	closeQuestionnaire(): void {
		document.location.href = '/';
	}

	renderResumeButtonTemplate(): TemplateResult {
		return html`<button
			type="button"
			@click=${() => {
				this.resumeLater();
			}}
			class="order-1 text-donkerblauw-100 underline focus:outline-none focus:ring-2 focus:ring-donkerblauw-75 focus:ring-offset-2 md:inline-flex md:items-center md:justify-center md:border md:border-donkerblauw-100 md:bg-white md:px-4 md:py-2 md:text-xl md:font-medium md:no-underline md:transition-colors md:duration-150 md:ease-in-out  md:hover:border-grijs-2  md:hover:bg-grijs-2"
		>
			${textItems.resumeLater}
		</button> `;
	}

	renderFormActionsTemplate(): TemplateResult {
		return html`
			<nav class="order-2 ml-auto flex flex-row-reverse gap-4">
				${this.renderNextButtonTemplate()}
				${this.data.previousQuestionId != null ? this.renderPreviousButtonTemplate() : ''}
			</nav>
			<div class="flex gap-4">${this.renderResumeButtonTemplate()} ${this.renderGoToReportButtonTemplate()}</div>
		`;
	}

	renderGoToReportButtonTemplate(): TemplateResult | null {
		if (!this.data?.canOpenReport || !this.showResultBtn) return null;
		return html`<button
			type="button"
			id="reportBtn"
			class="btn btn--secondary absolute -top-20 left-1/2 order-2 flex -translate-x-1/2 gap-2 whitespace-nowrap rounded-full md:static md:translate-x-0 md:rounded-none"
			@click=${() => {
				this.openReport();
			}}
			?disabled=${this.isLoading}
		>
			${textItems.toReport}
			<svg class="shrink-0" width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g>
					<g>
						<path
							d="M15 0H6.4C6.1 0 5.9 0.0999999 5.7 0.3L0.3 5.7C0.1 5.9 0 6.1 0 6.4V19C0 19.6 0.4 20 1 20H15C15.6 20 16 19.6 16 19V1C16 0.4 15.6 0 15 0ZM14 18H2V7H6C6.6 7 7 6.6 7 6V2H14V18Z"
							fill="#01689B"
						/>
						<path d="M12 9H4V10H12V9Z" fill="#01689B" />
						<path d="M12 12H4V13H12V12Z" fill="#01689B" />
						<path d="M12 15H4V16H12V15Z" fill="#01689B" />
					</g>
				</g>
			</svg>
		</button> `;
	}

	openReport(): void {
		const event = new CustomEvent(CustomEventNames.OpenReport, {
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	renderNextButtonTemplate(): TemplateResult {
		return html`<button
			type="submit"
			form="questionaireForm"
			class="btn btn--primary"
			@click=${() => {
				this.answerQuestion();
			}}
			?disabled=${this.isLoading || this.isDisabled}
		>
			<span class="hidden md:block">${textItems.next}</span>
			${this.isLoading && this.loadingButton === ButtonId.AnswerButton
				? html` ${this.loaderTemplate()} `
				: html`<svg
						class="md:hidden"
						width="16"
						height="24"
						viewBox="0 0 16 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						title="${textItems.icons.next}"
				  >
						<g>
							<path
								d="M1.70022 23.1C0.700224 22.1 0.300224 20.8 1.40022 19.8L9.10022 12.1L1.30022 4.30002C0.300224 3.30002 0.600224 2.00002 1.60022 1.00002C2.60022 1.7941e-05 3.90022 -0.399982 4.90022 0.700018L14.5002 10.3C15.5002 11.3 15.5002 12.9 14.5002 13.9L5.00022 23.4C4.00022 24.4 2.70022 24.1 1.70022 23.1Z"
								fill="white"
							/>
						</g>
				  </svg>`}
		</button>`;
	}

	renderPreviousButtonTemplate(): TemplateResult {
		return html`<button
			class="btn btn--primary"
			@click=${() => {
				this.openQuestion();
			}}
			?disabled=${this.isLoading}
		>
			<span class="hidden md:block">${textItems.previous}</span>
			${this.isLoading && this.loadingButton === ButtonId.OpenButton
				? html` ${this.loaderTemplate()} `
				: html`<svg
						class="rotate-180 md:hidden"
						width="16"
						height="24"
						viewBox="0 0 16 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						title="${textItems.icons.previous}"
				  >
						<g>
							<path
								d="M1.70022 23.1C0.700224 22.1 0.300224 20.8 1.40022 19.8L9.10022 12.1L1.30022 4.30002C0.300224 3.30002 0.600224 2.00002 1.60022 1.00002C2.60022 1.7941e-05 3.90022 -0.399982 4.90022 0.700018L14.5002 10.3C15.5002 11.3 15.5002 12.9 14.5002 13.9L5.00022 23.4C4.00022 24.4 2.70022 24.1 1.70022 23.1Z"
								fill="white"
							/>
						</g>
				  </svg>`}
		</button>`;
	}

	render(): TemplateResult {
		return html`<aside class="fixed bottom-0 left-0 right-0 bg-white py-4 shadow-aside print:hidden">
			<section class="relative mx-auto flex max-w-3xl px-6">
				${this.data?.question == null && !this.data?.mustOpenReport
					? this.renderStartButtonTemplate()
					: this.data?.question != null
					? this.renderFormActionsTemplate()
					: this.renderReportButtonTemplate()}
			</section>
		</aside> `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-action-bar': SzwBaActionBar;
	}
}
